import { useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

export async function moveProperty(propertyId, toClientId) {
  const { data } = await API.post(`/properties/${propertyId}/move`, { to_client_id: toClientId });
  toast.success(`${data.message}${data.linked_records ? ` · ${data.linked_records} linked record${data.linked_records === 1 ? "" : "s"} follow it` : ""}`);
  return data;
}

export async function bulkMoveProperties(propertyIds, toClientId) {
  const { data } = await API.post("/properties/bulk-move", { property_ids: propertyIds, to_client_id: toClientId });
  if (data.moved.length) toast.success(data.message);
  else toast.error(data.skipped[0]?.reason || "Nothing was moved");
  if (data.moved.length && data.skipped.length) toast.warning(`Skipped: ${data.skipped.map((s) => `${s.name || "property"} — ${s.reason}`).join("; ")}`);
  return data;
}

/** Pick the client / sub-client (same family) one property — or a ticked batch — should move to. */
export default function MovePropertyDialog({ property, properties, targets, onClose, onMoved }) {
  const batch = properties && properties.length ? properties : [property];
  const single = batch.length === 1 ? batch[0] : null;
  const fromIds = new Set(batch.map((p) => p.client_id));
  const options = (targets || []).filter((t) => !(fromIds.size === 1 && fromIds.has(t.id)));
  const [to, setTo] = useState(options.length === 1 ? options[0].id : "");
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (!to) return toast.error("Pick where to move it");
    setBusy(true);
    try {
      const data = single ? await moveProperty(single.id, to) : await bulkMoveProperties(batch.map((p) => p.id), to);
      onMoved && onMoved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not move property"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="move-property-dialog">
        <DialogHeader><DialogTitle>{single ? <>Move “{single.name}”</> : <>Move {batch.length} properties</>}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {single ? (
            <p className="text-xs text-muted-foreground">{[single.address_line, single.postcode].filter(Boolean).join(", ")}. Its requests, jobs and history stay linked to the property.</p>
          ) : (
            <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto space-y-0.5 border border-border rounded-md p-2" data-testid="move-property-batch-list">
              {batch.map((p) => <li key={p.id} className="truncate">• {p.name} <span className="opacity-70">— {[p.address_line, p.postcode].filter(Boolean).join(", ")}</span></li>)}
            </ul>
          )}
          <div>
            <Label className="text-xs">Move to</Label>
            <select value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="move-property-target">
              <option value="">Choose a client or sub-client…</option>
              {options.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {!single && <p className="text-[11px] text-muted-foreground mt-1">Properties already on that account, or with a duplicate address there, are skipped and reported.</p>}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="move-property-cancel">Cancel</Button>
            <Button size="sm" onClick={go} disabled={busy || !to} data-testid="move-property-confirm">{busy ? "Moving…" : single ? "Move property" : `Move ${batch.length} properties`}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
